import React, { memo, useCallback, useMemo } from 'react';

import { Box } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DeleteIcon, PlusIcon } from '@sharedCR/coreDesignSystem/icons';
import ActionButton from 'app/shared/components/common/AddButton';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import {
  AutocompleteCDS,
  CheckboxCDS,
  TextFieldCDS,
} from 'app/shared/componentsRestyled/coreDesignSystem/form';
import clsx from 'clsx';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { COMMON_REQUEST_DATE_FORMAT } from 'constants/date';
import { DOC_FIELD_TYPES } from 'constants/documentFieldTypes';
import { getConditionalObject } from 'utils/data';
import { checkIsContainerType } from 'utils/documentFieldTypes';

import { DocumentTestIds } from '../../document.testIds';

import { checkNestedFieldsIsNotEmpty } from './EditExtractionFields.utils';

const datePickerSlotProps = {
  textField: {
    fullWidth: true,
    variant: 'outlined',
    margin: 'normal',
  },
};

// Below the table's last row, so it moves down as rows are added (LEN-194).
const addRowStyles = {
  display: 'flex',
  justifyContent: 'flex-end',
};

const fieldWithBoundingBoxStyles = {
  '& .MuiInputBase-root': {
    borderColor: ({ palette }) => palette.border.success,
  },
  '& .MuiCheckbox-root, & .MuiRadio-root': {
    color: ({ palette }) => palette.border.success,
  },
  '& .MuiFormControlLabel-label': {
    color: ({ palette }) => palette.border.success,
  },
};

const shouldComponentUpdate = (prevProps, nextProps) => {
  const { form, errors, id, disabled, isInputHighlighted } = prevProps;
  const {
    type: nextType,
    nestedFields: nextNestedFields,
    form: nextForm,
    errors: nextErrors,
    id: nextId,
    disabled: nextDisabled,
    isInputHighlighted: nextIsInputHighlighted,
  } = nextProps;
  const isContainerType = checkIsContainerType(nextType);

  if (isContainerType && nextNestedFields) {
    return false;
  }

  return (
    form[id] === nextForm[nextId] &&
    errors[id] === nextErrors[nextId] &&
    disabled === nextDisabled &&
    isInputHighlighted === nextIsInputHighlighted
  );
};

const EditExtractionField = ({
  id,
  type,
  isRequired = false,
  name,
  form,
  errors,
  nestedFields,
  onChange,
  disabled = false,
  setInForm,
  onAdd = null,
  nestedGroups,
  onRemove = null,
  validateField,
  isInputHighlighted = false,
}) => {
  const isContainerType = checkIsContainerType(type);
  const isSingleDropDown = type === DOC_FIELD_TYPES.SINGLE_SELECTION_DROP_DOWN;
  const isMultipleDropDown = type === DOC_FIELD_TYPES.MULTIPLE_SELECTION_DROP_DOWN;
  const isCheckbox = type === DOC_FIELD_TYPES.BOOL;
  const isSingleDropDownVisible = isSingleDropDown && nestedFields?.length;
  const isMultipleDropDownVisible = isMultipleDropDown && nestedFields?.length && !!form[id];

  const isContainerNotEmpty = useMemo(
    () => !!nestedFields?.some(checkNestedFieldsIsNotEmpty),
    [nestedFields],
  );

  const value = useMemo(() => {
    const savedValue = form?.[id];

    if (isMultipleDropDownVisible) {
      const savedIds = savedValue.map(({ id }) => id);

      return savedIds.length ? nestedFields.filter(({ id }) => savedIds.includes(id)) : [];
    }

    if (isSingleDropDownVisible) {
      const savedOptionId = savedValue?.id;

      return nestedFields.find(field => field.id === savedOptionId);
    }

    if (isCheckbox) {
      return !!savedValue;
    }

    return savedValue;
  }, [isMultipleDropDownVisible, isSingleDropDownVisible, form, id, nestedFields, isCheckbox]);

  const onChangeDate = useCallback(
    date => {
      const dateMoment = dayjs(date);

      if (dateMoment.isValid()) {
        const formattedDate = dateMoment.format(COMMON_REQUEST_DATE_FORMAT);

        setInForm(id, formattedDate);
        validateField(id, formattedDate);
      }

      if (!date && value) {
        setInForm(id, undefined);
        validateField(id, undefined);
      }
    },
    [id, setInForm, value, validateField],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  if (isMultipleDropDownVisible) {
    return (
      <AutocompleteCDS
        disableClearable
        multiple
        containerClassName="mb-24"
        dataTestId={DocumentTestIds.getExtractionFieldSelectId(id)}
        error={!!errors?.[id]}
        helperText={errors?.[id]}
        label={name}
        name={id}
        optionName="name"
        options={nestedFields}
        required={isRequired}
        value={value}
        onChange={onAutocompleteChange}
      />
    );
  }

  if (isSingleDropDownVisible) {
    return (
      <AutocompleteCDS
        containerClassName="mb-24"
        dataTestId={DocumentTestIds.getExtractionFieldSelectId(id)}
        error={!!errors?.[id]}
        helperText={errors?.[id]}
        label={name}
        name={id}
        options={nestedFields}
        required={isRequired}
        value={value || null}
        onChange={onAutocompleteChange}
      />
    );
  }

  const dynamicContainer = DOC_FIELD_TYPES.TABLE;

  if (type === dynamicContainer && isContainerNotEmpty) {
    return (
      <div className={clsx('mt-8 mb-16 pt-20 px-2')}>
        <div>{name}</div>
        <div className={clsx('px-2')}>
          {nestedFields.map(field => (
            <EditExtractionField
              {...field}
              disabled={disabled}
              errors={errors}
              form={form}
              isRequired={field.isRequired}
              key={field.id}
              setInForm={setInForm}
              validateField={validateField}
              onAdd={onAdd}
              onChange={onChange}
              onRemove={onRemove}
            />
          ))}
        </div>

        {!!nestedGroups &&
          Object.keys(nestedGroups).map(groupId => {
            return (
              <div className="mt-16 px-2" key={groupId}>
                {nestedGroups[groupId].map(field => (
                  <EditExtractionField
                    {...field}
                    disabled={disabled}
                    errors={errors}
                    form={form}
                    isRequired={field.isRequired}
                    key={field.id}
                    setInForm={setInForm}
                    validateField={validateField}
                    onAdd={onAdd}
                    onChange={onChange}
                    onRemove={onRemove}
                  />
                ))}
                <ActionButton
                  ariaLabel="Remove row"
                  data-group-id={groupId}
                  dataTestId={DocumentTestIds.getRemoveDynamicContainerButtonId(name)}
                  icon={DeleteIcon}
                  styleConfig="small"
                  onClick={onRemove}
                />
              </div>
            );
          })}

        <Box sx={addRowStyles}>
          <ActionButton
            ariaLabel="Add new row"
            data-id={id}
            dataTestId={DocumentTestIds.getAddNewDynamicContainerButtonId(name)}
            icon={PlusIcon}
            styleConfig="small"
            onClick={onAdd}
          />
        </Box>
      </div>
    );
  }

  if (type === DOC_FIELD_TYPES.CONTAINER && isContainerNotEmpty) {
    return (
      <Box
        className={clsx('mt-8 mb-16')}
        sx={isInputHighlighted ? fieldWithBoundingBoxStyles : null}
      >
        <div>{name}</div>
        {nestedFields.map(field => (
          <EditExtractionField
            {...field}
            disabled={disabled}
            errors={errors}
            form={form}
            isRequired={field.isRequired}
            key={field.id}
            setInForm={setInForm}
            validateField={validateField}
            onAdd={onAdd}
            onChange={onChange}
            onRemove={onRemove}
          />
        ))}
      </Box>
    );
  }

  if (isContainerType || isSingleDropDown || isMultipleDropDown) {
    return null;
  }

  if (type === DOC_FIELD_TYPES.BOOL) {
    return (
      <div>
        <CheckboxCDS
          containerClassName="mt-12 mb-24"
          dataTestId={DocumentTestIds.getExtractionFieldCheckboxId(id)}
          disabled={disabled}
          error={!!errors[id]}
          helperText={errors[id]}
          label={name}
          name={id}
          required={isRequired}
          sx={isInputHighlighted ? fieldWithBoundingBoxStyles : null}
          value={!!value}
          onChange={onChange}
        />
      </div>
    );
  }

  // TODO: update to DateField, when Document Data will be refactored
  if (type === DOC_FIELD_TYPES.DATE) {
    return (
      <div>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            className="mt-12 mb-24"
            disabled={disabled}
            format="MM-DD-YYYY"
            label={name}
            name={id}
            slotProps={datePickerSlotProps}
            sx={isInputHighlighted ? fieldWithBoundingBoxStyles : null}
            value={value ? dayjs(value) : null}
            onChange={onChangeDate}
          />
        </LocalizationProvider>
      </div>
    );
  }

  return (
    <TextFieldCDS
      fullWidth
      className="mb-24"
      dataTestId={DocumentTestIds.getExtractionFieldInputId(id)}
      disabled={disabled}
      error={!!errors[id]}
      helperText={errors[id]}
      label={name}
      name={id}
      required={isRequired}
      sx={getConditionalObject(isInputHighlighted, fieldWithBoundingBoxStyles)}
      value={value}
      variant="outlined"
      onChange={onChange}
    />
  );
};

EditExtractionField.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  form: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  setInForm: PropTypes.func.isRequired,
  validateField: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  isInputHighlighted: PropTypes.bool,
  isRequired: PropTypes.bool,
  disabled: PropTypes.bool,
  nestedFields: PropTypes.arrayOf(PropTypes.object),
  nestedGroups: PropTypes.object,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
};

export default memo(EditExtractionField, shouldComponentUpdate);
